import {Component, computed, effect, inject, Input, OnChanges, OnInit, signal, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, EnergyType, Interval} from './base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {Chart, ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import annotationPlugin from 'chartjs-plugin-annotation';
import {CommonModule} from '@angular/common';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {environment} from '../../../../environments/environment';
import {rxResource} from '@angular/core/rxjs-interop';
import {SunResponse} from '../../../../api/models/sun-response';
import {LocationStateService} from '../../../../common/services/location-state.service';
import {getAverageEnergyStatus, getPlannedEnergyStatus} from './average-energy-response.helper';

@Component({
  selector: 'app-grid-day-chart',
  templateUrl: './chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, CommonModule],
})
export class GridDayChartComponent extends BaseChartComponent implements OnChanges {
  override interval: Interval = 'day'; // Define input with default value
  type: EnergyType = 'grid'; // Define input with default value
  protected override get summaryType(): EnergyType { return 'grid'; }

  @Input() set date(value: Date) {
    this._date.set(value);
  }

  get date(): Date {
    return this._date();
  }

  private _date = signal<Date>(new Date());
  @Input() data: AverageEnergyResponse | undefined;


  // Weather resource for sunrise/sunset data - automatically reloads when date changes
  // Using custom loader that directly accesses the current date from the component
  weatherResource = rxResource<SunResponse, any>({
    params: computed(() => ({body: {date: this._date().toISOString()}})),
    defaultValue: environment.showDefault ? {
      'sunrise': this._date().toISOString(),
      'sunset': this._date().toISOString() } : { 'sunrise': undefined, 'sunset': undefined},
    stream: this.sunDataLoader,
  });

  private sunriseSunsetEffect = effect(() => {
    const sun = this.weatherResource.value();
    this.removeAnnotations();

    if (sun?.sunrise) {
      this.lineChartOptions = this.addVerticalLine(this.lineChartOptions, new Date(sun.sunrise), 'Sonnenaufgang', 'rgb(128,128,128)');
    }
    if (sun?.sunset) {
      this.lineChartOptions = this.addVerticalLine(this.lineChartOptions, new Date(sun.sunset), 'Sonnenuntergang', 'rgb(128,128,128)');
    }
    // Subtle marker for the current time (only shown when viewing today)
    this.lineChartOptions = this.addCurrentTimeLine(this.lineChartOptions, this._date());
    // refresh options signal so chart receives new reference
    this.chartOptions.set(this.lineChartOptions);
    // trigger chart to re-render with new options
    if (this.chart) {
      this.chart.update();
    }
  });

  lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    elements: {
      line: {
        tension: 0, // No interpolation, sharp lines
        fill: true,
      },
      point: {
        radius: 0,
        hoverRadius: 0,
      },
    },
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        min: -10,
        max: 10,
        ticks: {
          stepSize: 1, // Display every integer
          precision: 0,
        },
        border: {
          display: false,
        },
      },
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        ticks:{
          minRotation: this.isMobile() ? 90 : 0,
          maxRotation: this.isMobile() ? 90 : 0,
          autoSkip: true,
          maxTicksLimit: this.isMobile() ? 12 : 24, // Fewer labels on mobile
          padding: this.isMobile() ? 4 : 8, // Less padding on mobile
          // Add mobile-specific font sizing
          font: {
            size: this.isMobile() ? 10 : 12
          }
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  private readonly locationStateService = inject(LocationStateService);

  private readonly locationEffect = effect(() => {
    const range = this.locationStateService.currentLocation()?.displayChart?.grid;

    if (range) {
      const tickStep = this.getSymmetricAxisTickStep(
        Math.max(Math.abs(range.minkWh ?? 0), Math.abs(range.maxkWh ?? 0)),
      );
      this.lineChartOptions = {
        ...this.lineChartOptions,
        scales: {
          ...this.lineChartOptions.scales,
          ['y']: {
            ...this.lineChartOptions.scales?.['y'],
            min: range.minkWh ?? undefined,
            max: range.maxkWh ?? undefined,
            ticks: {
              ...((this.lineChartOptions.scales?.['y'] as any)?.ticks ?? {}),
              stepSize: tickStep,
              precision: 0,
            },
          },
        },
      };
      this.chartOptions.set(this.lineChartOptions);
      if (this.chart) {
        this.chart.update();
      }
    }
  });

  constructor() {
    super();
    // Register the annotation plugin
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Performance Grid Day chart received change', changes);
    if (changes['data'] || changes['type'] || changes['date'] || changes['interval']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.initializeChartData();
  }

  private initializeChartData() {
    if (!this.data) {
      return; // Exit if no data is available
    }

    const datasets: any[] = [];
    const allLabels: Date[][] = [];

    // Process both positive and negative grid power
    const positiveData = getAverageEnergyStatus(this.data, 'gridPositive');
    const negativeData = getAverageEnergyStatus(this.data, 'gridNegative');

    // Process positive power data
    Object.entries(positiveData?.deviceTimeValues ?? {}).forEach(([key, timeValues]) => {
      // Type guard to ensure timeValues is an array of DateTimeValue
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
      // Ensure timeValues is complete
      const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, 'zero', positiveData?.timeBucket);
      // Ensure values are positive
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({
        ...tv,
        value: tv.value !== undefined && tv.value !== null ? Math.abs(tv.value) : tv.value,
      }));

      // Extract labels from this dataset
      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      allLabels.push(datasetLabels);

      const legendLabel = positiveData?.deviceNames?.[key] || key;
      datasets.push({
        label: `Einkauf`,
        data: rawTimeValues.map((v) => v.value),
        borderColor: '#333333', // Green for positive
        backgroundColor: '#333333',
        pointRadius: 0,
        pointHoverRadius: 0,
        cubicInterpolationMode: 'monotone',
        spanGaps: false,
        borderWidth: 1,
        stack: 'stack1',
        base: 0,
      });

    });

    // Process negative power data
    Object.entries(negativeData?.deviceTimeValues ?? {}).forEach(([key, timeValues]) => {
      // Type guard to ensure timeValues is an array of DateTimeValue
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
      // Ensure timeValues is complete
      const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, 'zero', negativeData?.timeBucket);
      // Ensure all values are negative
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({
        ...tv,
        value: tv.value !== undefined && tv.value !== null ? -Math.abs(tv.value) : tv.value,
      }));

      // Extract labels from this dataset
      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      allLabels.push(datasetLabels);

      const legendLabel = negativeData?.deviceNames?.[key] || key;
      datasets.push({
        label: `Verkauf`,
        data: rawTimeValues.map((v) => v.value),
        borderColor: '#a0a0a0', // Red for negative #F44336 //dunkles grün: #3d6c26 //dunkles rot 9c1f18// blau: 00B6EE
        backgroundColor: '#a0a0a0',
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.1, // Smooth interpolation between points
        cubicInterpolationMode: 'monotone',
        spanGaps: false,
        borderWidth: 1,
        base: 0,
      });

    });

    // Forecast counterparts, drawn in much lighter shades of the same grey/black and
    // in their own stacks so measured and planned values of one day sit side by side.
    this.appendPlannedDatasets({datasets, allLabels}, getPlannedEnergyStatus(this.data, 'gridPositive'), {
      date: this.date,
      interval: this.interval,
      fillMode: 'zero',
      timeBucket: positiveData?.timeBucket,
      color: () => '#333333',
      label: () => this.getPlannedLabel('Einkauf'),
      transform: (item) => {
        item.value = item.value != null ? Math.abs(item.value) : item.value;
      },
      dataset: () => ({
        cubicInterpolationMode: 'monotone',
        stack: 'plannedStackPositive',
        base: 0,
      }),
    });

    this.appendPlannedDatasets({datasets, allLabels}, getPlannedEnergyStatus(this.data, 'gridNegative'), {
      date: this.date,
      interval: this.interval,
      fillMode: 'zero',
      timeBucket: negativeData?.timeBucket,
      color: () => '#a0a0a0',
      label: () => this.getPlannedLabel('Verkauf'),
      transform: (item) => {
        item.value = item.value != null ? -Math.abs(item.value) : item.value;
      },
      dataset: () => ({
        tension: 0.1,
        cubicInterpolationMode: 'monotone',
        stack: 'plannedStackNegative',
        base: 0,
      }),
    });

    // Process all labels to ensure they match
    const labels = this.processLabels(allLabels);
    this.updateChart(labels, datasets);
  }

  private updateChart(labels: any[], datasets: any[]) {
    this.chartType.set('line'); // Set chart type to bar
    // Re-apply the current-time marker on every rebuild so it survives view switches
    this.lineChartOptions = this.addCurrentTimeLine(this.lineChartOptions, this._date());
    this.chartOptions.set(this.lineChartOptions);
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private removeAnnotations() {
    if (this.lineChartOptions.plugins?.annotation) {
      this.lineChartOptions.plugins.annotation.annotations = {};
      this.lineChartOptions = {...this.lineChartOptions};
    }

    if (this.chart && this.chart.options && this.chart.options.plugins && this.chart.options.plugins.annotation) {
      this.chart.options.plugins.annotation.annotations = {};
    }

    this.chartOptions.set(this.lineChartOptions);

    if (this.chart) {
      this.chart.update();
    }
  }
}
